import React, { useEffect, useState } from "react";
import api from "../api/client";

/**
 * Per-customer cloud connector management. Lives on the CustomerDetail
 * "Cloud" tab (admin-only). Supports Azure today; AWS / GCP / extended
 * M365 land in subsequent sessions and become additional entries in
 * the platform dropdown when the backend's supported-platforms list
 * grows.
 *
 * Credentials are write-only on the wire — existing connectors show
 * "Credentials set" but the secrets never come back from the server.
 */

interface Connector {
  id: number;
  customer_id: number;
  platform: string;
  platform_label: string;
  name: string;
  enabled: boolean;
  has_credentials: boolean;
  last_test_at: string | null;
  last_test_status: string | null;
  last_test_error: string | null;
  capabilities: Record<string, unknown> | null;
}

interface PlatformOption {
  platform: string;
  label: string;
}

interface AzureCreds {
  tenant_id: string;
  client_id: string;
  client_secret: string;
  subscription_id: string;
}

interface AwsCreds {
  access_key_id: string;
  secret_access_key: string;
  default_region: string;
  assume_role_arn: string;
  external_id: string;
}

interface GcpCreds {
  service_account_json: string;
  project_id: string;
}

interface GwsCreds {
  service_account_json: string;
  delegated_admin_email: string;
  customer_domain: string;
}

const EMPTY_AZURE: AzureCreds = {
  tenant_id: "",
  client_id: "",
  client_secret: "",
  subscription_id: "",
};

const EMPTY_AWS: AwsCreds = {
  access_key_id: "",
  secret_access_key: "",
  default_region: "us-east-1",
  assume_role_arn: "",
  external_id: "",
};

const EMPTY_GCP: GcpCreds = {
  service_account_json: "",
  project_id: "",
};

const EMPTY_GWS: GwsCreds = {
  service_account_json: "",
  delegated_admin_email: "",
  customer_domain: "",
};

export default function CloudConnectorsCard({ customerId }: { customerId: number }) {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [platforms, setPlatforms] = useState<PlatformOption[]>([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Connector | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftPlatform, setDraftPlatform] = useState("azure");
  const [draftEnabled, setDraftEnabled] = useState(true);
  const [draftAzure, setDraftAzure] = useState<AzureCreds>(EMPTY_AZURE);
  const [draftAws, setDraftAws] = useState<AwsCreds>(EMPTY_AWS);
  const [draftGcp, setDraftGcp] = useState<GcpCreds>(EMPTY_GCP);
  const [draftGws, setDraftGws] = useState<GwsCreds>(EMPTY_GWS);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, string>>({});

  const load = () => {
    api
      .get("/api/v1/cloud-connectors", { params: { customer_id: customerId } })
      .then((r) => setConnectors(r.data))
      .catch(() => setConnectors([]));
  };
  useEffect(() => {
    load();
    api
      .get("/api/v1/cloud-connectors/supported-platforms")
      .then((r) => setPlatforms(r.data || []))
      .catch(() => setPlatforms([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const reset = () => {
    setCreating(false);
    setEditing(null);
    setDraftName("");
    setDraftPlatform(platforms[0]?.platform || "azure");
    setDraftEnabled(true);
    setDraftAzure(EMPTY_AZURE);
    setDraftAws(EMPTY_AWS);
    setDraftGcp(EMPTY_GCP);
    setDraftGws(EMPTY_GWS);
    setError("");
  };

  const openCreate = () => {
    reset();
    setCreating(true);
  };

  const openEdit = (c: Connector) => {
    reset();
    setEditing(c);
    setDraftName(c.name);
    setDraftPlatform(c.platform);
    setDraftEnabled(c.enabled);
    // Creds stay blank — admin only re-enters when rotating.
  };

  const save = async () => {
    setError("");
    if (!draftName.trim()) return setError("Name is required");
    const platformValid = platforms.some((p) => p.platform === draftPlatform);
    if (!platformValid) return setError(`Platform '${draftPlatform}' not supported`);

    let creds: Record<string, string> | undefined;
    if (draftPlatform === "azure") {
      const a = draftAzure;
      const hasAny = a.tenant_id || a.client_id || a.client_secret || a.subscription_id;
      if (creating && !hasAny) {
        return setError("Azure credentials required when creating a connector");
      }
      if (hasAny) {
        const missing: string[] = [];
        if (!a.tenant_id.trim()) missing.push("tenant_id");
        if (!a.client_id.trim()) missing.push("client_id");
        if (!a.client_secret.trim()) missing.push("client_secret");
        if (!a.subscription_id.trim()) missing.push("subscription_id");
        if (missing.length) return setError(`Azure creds missing: ${missing.join(", ")}`);
        creds = {
          tenant_id: a.tenant_id.trim(),
          client_id: a.client_id.trim(),
          client_secret: a.client_secret,
          subscription_id: a.subscription_id.trim(),
        };
      }
    } else if (draftPlatform === "aws") {
      const a = draftAws;
      const hasAny =
        a.access_key_id ||
        a.secret_access_key ||
        a.assume_role_arn ||
        a.external_id;
      if (creating && !hasAny) {
        return setError("AWS credentials required when creating a connector");
      }
      if (hasAny) {
        const missing: string[] = [];
        if (!a.access_key_id.trim()) missing.push("access_key_id");
        if (!a.secret_access_key.trim()) missing.push("secret_access_key");
        if (!a.default_region.trim()) missing.push("default_region");
        if (missing.length) return setError(`AWS creds missing: ${missing.join(", ")}`);
        creds = {
          access_key_id: a.access_key_id.trim(),
          secret_access_key: a.secret_access_key,
          default_region: a.default_region.trim(),
        };
        if (a.assume_role_arn.trim()) creds.assume_role_arn = a.assume_role_arn.trim();
        if (a.external_id.trim()) creds.external_id = a.external_id.trim();
      }
    } else if (draftPlatform === "gcp") {
      const g = draftGcp;
      const hasAny = g.service_account_json || g.project_id;
      if (creating && !hasAny) {
        return setError("GCP credentials required when creating a connector");
      }
      if (hasAny) {
        const missing: string[] = [];
        if (!g.service_account_json.trim()) missing.push("service_account_json");
        if (!g.project_id.trim()) missing.push("project_id");
        if (missing.length) return setError(`GCP creds missing: ${missing.join(", ")}`);
        try {
          JSON.parse(g.service_account_json);
        } catch {
          return setError("service_account_json isn't valid JSON");
        }
        creds = {
          service_account_json: g.service_account_json,
          project_id: g.project_id.trim(),
        };
      }
    } else if (draftPlatform === "gworkspace") {
      const w = draftGws;
      const hasAny =
        w.service_account_json ||
        w.delegated_admin_email ||
        w.customer_domain;
      if (creating && !hasAny) {
        return setError("Workspace credentials required when creating a connector");
      }
      if (hasAny) {
        const missing: string[] = [];
        if (!w.service_account_json.trim()) missing.push("service_account_json");
        if (!w.delegated_admin_email.trim()) missing.push("delegated_admin_email");
        if (!w.customer_domain.trim()) missing.push("customer_domain");
        if (missing.length) return setError(`Workspace creds missing: ${missing.join(", ")}`);
        try {
          JSON.parse(w.service_account_json);
        } catch {
          return setError("service_account_json isn't valid JSON");
        }
        creds = {
          service_account_json: w.service_account_json,
          delegated_admin_email: w.delegated_admin_email.trim(),
          customer_domain: w.customer_domain.trim(),
        };
      }
    }

    setSaving(true);
    try {
      if (editing) {
        const body: Record<string, unknown> = {
          name: draftName.trim(),
          enabled: draftEnabled,
        };
        if (creds) body.credentials = creds;
        await api.put(`/api/v1/cloud-connectors/${editing.id}`, body);
      } else {
        await api.post("/api/v1/cloud-connectors", {
          customer_id: customerId,
          platform: draftPlatform,
          name: draftName.trim(),
          enabled: draftEnabled,
          credentials: creds || {},
        });
      }
      reset();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: Connector) => {
    if (!window.confirm(`Delete cloud connector "${c.name}" (${c.platform_label})?`)) return;
    try {
      await api.delete(`/api/v1/cloud-connectors/${c.id}`);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Delete failed");
    }
  };

  const testConn = async (c: Connector) => {
    setTesting(c.id);
    setTestResults((r) => ({ ...r, [c.id]: "" }));
    try {
      const r = await api.post(`/api/v1/cloud-connectors/${c.id}/test`);
      setTestResults((prev) => ({
        ...prev,
        [c.id]: r.data.ok ? `OK — ${r.data.message}` : `Failed — ${r.data.message}`,
      }));
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setTestResults((prev) => ({
        ...prev,
        [c.id]: `Error: ${err.response?.data?.detail || "test failed"}`,
      }));
    } finally {
      setTesting(null);
    }
  };

  const isOpen = creating || editing !== null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Cloud connectors</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Per-customer credentials the AI / approval queue uses to act against the customer's
            cloud + identity platforms (Azure, AWS, GCP, Google Workspace). Writes always route
            through the AI Actions approval queue.
          </p>
        </div>
        {!isOpen && (
          <button
            onClick={openCreate}
            className="text-xs px-3 py-1.5 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark"
          >
            Add connector
          </button>
        )}
      </div>

      {!isOpen && (
        <div className="space-y-2">
          {connectors.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">No connectors configured.</p>
          ) : (
            connectors.map((c) => (
              <div
                key={c.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {c.name}{" "}
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 ml-1">
                      {c.platform_label}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {c.enabled ? "Enabled" : "Disabled"} ·{" "}
                    {c.has_credentials ? "Credentials set" : "No credentials"}
                    {c.last_test_status && (
                      <>
                        {" · Last test "}
                        <span
                          className={
                            c.last_test_status === "success"
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }
                        >
                          {c.last_test_status}
                        </span>
                      </>
                    )}
                  </div>
                  {testResults[c.id] && (
                    <div className="text-xs mt-1 text-gray-700 dark:text-gray-300">{testResults[c.id]}</div>
                  )}
                  {c.last_test_status === "failure" && c.last_test_error && !testResults[c.id] && (
                    <div className="text-xs mt-1 text-red-600 dark:text-red-400 truncate">{c.last_test_error}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => testConn(c)}
                    disabled={testing === c.id}
                    className="text-xs text-gray-700 dark:text-gray-300 hover:underline font-medium disabled:opacity-50"
                  >
                    {testing === c.id ? "Testing…" : "Test"}
                  </button>
                  <button
                    onClick={() => openEdit(c)}
                    className="text-xs text-brand dark:text-brand-light hover:underline font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(c)}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {isOpen && (
        <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-slate-50/50 dark:bg-gray-900/50">
          <Row label="Name (admin label)">
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="w-full border rounded-lg px-3 py-1.5 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              placeholder="Prod subscription"
            />
          </Row>
          <Row label="Platform">
            <select
              value={draftPlatform}
              onChange={(e) => setDraftPlatform(e.target.value)}
              disabled={!!editing}
              className="w-full border rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            >
              {platforms.map((p) => (
                <option key={p.platform} value={p.platform}>
                  {p.label}
                </option>
              ))}
            </select>
          </Row>
          {draftPlatform === "azure" && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                Create a Service Principal in the customer's tenant:{" "}
                <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">
                  az ad sp create-for-rbac --role Contributor --scopes
                  /subscriptions/&lt;SUB&gt;
                </code>
                . Paste the four IDs it emits below.
                {editing && editing.has_credentials && (
                  <span className="block mt-1 italic">
                    Leave all four fields blank to keep the existing credentials.
                  </span>
                )}
              </p>
              <Row label="Tenant ID">
                <input
                  value={draftAzure.tenant_id}
                  onChange={(e) =>
                    setDraftAzure({ ...draftAzure, tenant_id: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </Row>
              <Row label="Client ID (appId)">
                <input
                  value={draftAzure.client_id}
                  onChange={(e) =>
                    setDraftAzure({ ...draftAzure, client_id: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </Row>
              <Row label="Client secret (password)">
                <input
                  type="password"
                  value={draftAzure.client_secret}
                  onChange={(e) =>
                    setDraftAzure({ ...draftAzure, client_secret: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  autoComplete="new-password"
                />
              </Row>
              <Row label="Subscription ID">
                <input
                  value={draftAzure.subscription_id}
                  onChange={(e) =>
                    setDraftAzure({ ...draftAzure, subscription_id: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </Row>
            </>
          )}
          {draftPlatform === "aws" && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                Create an IAM user with PowerUserAccess (or a custom least-privilege
                role) in the customer's AWS account and paste the access key + secret
                below. For cross-account MSP setups, fill in <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">assume_role_arn</code>{" "}
                and (optional) <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">external_id</code>; the
                IAM user only needs <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">sts:AssumeRole</code>{" "}
                on that target role.
                {editing && editing.has_credentials && (
                  <span className="block mt-1 italic">
                    Leave all fields blank to keep the existing credentials.
                  </span>
                )}
              </p>
              <Row label="Access key ID">
                <input
                  value={draftAws.access_key_id}
                  onChange={(e) =>
                    setDraftAws({ ...draftAws, access_key_id: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="AKIA..."
                />
              </Row>
              <Row label="Secret access key">
                <input
                  type="password"
                  value={draftAws.secret_access_key}
                  onChange={(e) =>
                    setDraftAws({ ...draftAws, secret_access_key: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  autoComplete="new-password"
                />
              </Row>
              <Row label="Default region">
                <input
                  value={draftAws.default_region}
                  onChange={(e) =>
                    setDraftAws({ ...draftAws, default_region: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="us-east-1"
                />
              </Row>
              <Row label="Assume role ARN (optional)">
                <input
                  value={draftAws.assume_role_arn}
                  onChange={(e) =>
                    setDraftAws({ ...draftAws, assume_role_arn: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="arn:aws:iam::123456789012:role/aerogauge-rmm"
                />
              </Row>
              <Row label="External ID (optional)">
                <input
                  value={draftAws.external_id}
                  onChange={(e) =>
                    setDraftAws({ ...draftAws, external_id: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </Row>
            </>
          )}
          {draftPlatform === "gcp" && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                Create a service account in the customer's GCP project, grant it the roles
                you want (start with{" "}
                <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">roles/compute.instanceAdmin.v1</code>{" "}
                + <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">roles/storage.objectViewer</code>),
                generate a JSON key, and paste the full JSON below.
                {editing && editing.has_credentials && (
                  <span className="block mt-1 italic">
                    Leave both fields blank to keep the existing credentials.
                  </span>
                )}
              </p>
              <Row label="Project ID">
                <input
                  value={draftGcp.project_id}
                  onChange={(e) =>
                    setDraftGcp({ ...draftGcp, project_id: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="my-customer-prod"
                />
              </Row>
              <Row label="Service account JSON key">
                <textarea
                  value={draftGcp.service_account_json}
                  onChange={(e) =>
                    setDraftGcp({ ...draftGcp, service_account_json: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-xs font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  rows={8}
                  placeholder='{"type": "service_account", "project_id": "...", "private_key_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----\n...", "client_email": "...", ...}'
                  autoComplete="off"
                  spellCheck={false}
                />
              </Row>
            </>
          )}
          {draftPlatform === "gworkspace" && (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                Create a service account in any GCP project, then grant{" "}
                <strong>domain-wide delegation</strong> for it in the customer's Workspace
                admin (Security → API controls → Domain-wide Delegation) with scopes{" "}
                <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">admin.directory.user</code>{" "}
                + <code className="bg-slate-200 dark:bg-gray-700 dark:text-gray-100 px-1 rounded">admin.directory.group</code>.
                Paste the SA JSON key below + tell us which super-admin user it
                should impersonate.
                {editing && editing.has_credentials && (
                  <span className="block mt-1 italic">
                    Leave all fields blank to keep the existing credentials.
                  </span>
                )}
              </p>
              <Row label="Customer domain">
                <input
                  value={draftGws.customer_domain}
                  onChange={(e) =>
                    setDraftGws({ ...draftGws, customer_domain: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="customer.com"
                />
              </Row>
              <Row label="Delegated admin email">
                <input
                  value={draftGws.delegated_admin_email}
                  onChange={(e) =>
                    setDraftGws({ ...draftGws, delegated_admin_email: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  placeholder="admin@customer.com"
                />
              </Row>
              <Row label="Service account JSON key">
                <textarea
                  value={draftGws.service_account_json}
                  onChange={(e) =>
                    setDraftGws({ ...draftGws, service_account_json: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-1.5 text-xs font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  rows={8}
                  placeholder='{"type": "service_account", ...}'
                  autoComplete="off"
                  spellCheck={false}
                />
              </Row>
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pt-1">
            <input
              type="checkbox"
              checked={draftEnabled}
              onChange={(e) => setDraftEnabled(e.target.checked)}
            />
            Enabled
          </label>
          {error && (
            <p className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={save}
              disabled={saving}
              className="text-xs px-3 py-1.5 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50"
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Create connector"}
            </button>
            <button
              onClick={reset}
              className="text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
